import { type ActivityTypeDto, type FleetSettings, type MeDto, OBLIGATION_RULES, type ObligationRule } from '@bqcrm/shared';
import { Badge, Button, Group, Modal, NumberInput, Select, SimpleGrid, Stack, Switch, Table, TagsInput, Text, TextInput } from '@mantine/core';
import { IconPlus } from '@tabler/icons-react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { api } from '../../api';
import { can } from '../../auth';
import { PageHeader } from '../../components/PageHeader';
import { QueryState } from '../../components/QueryState';
import { EmptyState, SectionCard } from '../../components/ui';
import { notifyError, notifyOk } from '../../notify';
import { FleetShell, kmPl, useActivityTypes, useFleetSettings } from './common';

const num = (v: string | number) => (typeof v === 'number' ? v : null);

function TypeModal({ type, onClose }: { type: ActivityTypeDto | null; onClose: () => void }) {
  const qc = useQueryClient();
  const [name, setName] = useState(type?.name ?? '');
  const [rule, setRule] = useState<ObligationRule>(type?.rule ?? 'data');
  const [everyMonths, setEveryMonths] = useState<number | null>(type?.everyMonths ?? null);
  const [everyKm, setEveryKm] = useState<number | null>(type?.everyKm ?? null);
  const [active, setActive] = useState(type?.active ?? true);
  const save = useMutation({
    mutationFn: () => api<ActivityTypeDto[]>(type ? `/fleet/activity-types/${type.id}` : '/fleet/activity-types', { method: type ? 'PUT' : 'POST', body: { name: name.trim(), rule, everyMonths, everyKm, active } }),
    onSuccess: (rows) => {
      qc.setQueryData(['fleet', 'activity-types'], rows);
      notifyOk(type ? 'Zapisano rodzaj czynności' : 'Dodano rodzaj czynności');
      onClose();
    },
    onError: notifyError,
  });
  return (
    <Modal opened onClose={onClose} title={type ? 'Edycja rodzaju czynności' : 'Nowy rodzaj czynności'}>
      <Stack gap="sm">
        <TextInput label="Nazwa" required value={name} onChange={(e) => setName(e.currentTarget.value)} data-autofocus />
        <Select label="Domyślny sposób wyznaczania" data={Object.entries(OBLIGATION_RULES).map(([value, label]) => ({ value, label }))} value={rule} onChange={(x) => x && setRule(x as ObligationRule)} allowDeselect={false} />
        <Text size="xs" c="dimmed">
          Podpowiedź cyklu nie jest stosowana automatycznie — zarządzający wpisuje cykl w konkretnej czynności pojazdu. Nie kodujemy zaleceń producentów ani przepisów.
        </Text>
        <Group grow>
          <NumberInput label="Podpowiedź: co ile miesięcy" min={1} max={240} allowDecimal={false} value={everyMonths ?? ''} onChange={(x) => setEveryMonths(num(x))} />
          <NumberInput label="Podpowiedź: co ile km" min={100} thousandSeparator=" " allowDecimal={false} value={everyKm ?? ''} onChange={(x) => setEveryKm(num(x))} />
        </Group>
        <Switch label="Aktywny (do wyboru w nowych czynnościach)" checked={active} onChange={(e) => setActive(e.currentTarget.checked)} />
        <Group justify="flex-end">
          <Button variant="default" onClick={onClose}>
            Anuluj
          </Button>
          <Button disabled={name.trim().length < 2} loading={save.isPending} onClick={() => save.mutate()}>
            Zapisz
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
}

const toNums = (v: string[]) => [...new Set(v.map((x) => Number(x.replace(/\s/g, ''))).filter((n) => Number.isInteger(n) && n > 0))].sort((a, b) => b - a);

function ThresholdsCard({ settings, editable }: { settings: FleetSettings; editable: boolean }) {
  const qc = useQueryClient();
  const [s, setS] = useState(settings);
  useEffect(() => setS(settings), [settings]);
  const dirty = JSON.stringify(s) !== JSON.stringify(settings);
  const save = useMutation({
    mutationFn: () => api<FleetSettings>('/fleet/settings', { method: 'PUT', body: s }),
    onSuccess: (r) => {
      qc.setQueryData(['fleet', 'settings'], r);
      void qc.invalidateQueries({ queryKey: ['fleet'] });
      notifyOk('Zapisano ustawienia floty');
    },
    onError: notifyError,
  });
  const set = (p: Partial<FleetSettings>) => setS((x) => ({ ...x, ...p }));
  return (
    <SectionCard
      title="Progi i przypomnienia"
      actions={
        editable && (
          <Button size="xs" disabled={!dirty} loading={save.isPending} onClick={() => save.mutate()}>
            Zapisz
          </Button>
        )
      }
    >
      <Stack gap="sm">
        <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="sm">
          <TagsInput label="Przypomnienia — dni przed terminem" description="np. 30 i 7; Enter po każdej liczbie" disabled={!editable} value={s.dueDays.map(String)} onChange={(v) => set({ dueDays: toNums(v) })} />
          <TagsInput label="Przypomnienia — km przed limitem" description="np. 2000 i 500" disabled={!editable} value={s.dueKm.map(String)} onChange={(v) => set({ dueKm: toNums(v) })} />
          <NumberInput label="Odczyt przebiegu nieaktualny po (dniach)" min={1} max={730} allowDecimal={false} disabled={!editable} value={s.staleDays} onChange={(x) => typeof x === 'number' && set({ staleDays: x })} />
          <NumberInput label="Podejrzany przyrost przebiegu (km na dobę)" min={50} max={10_000} thousandSeparator=" " allowDecimal={false} disabled={!editable} value={s.maxKmPerDay} onChange={(x) => typeof x === 'number' && set({ maxKmPerDay: x })} />
          <NumberInput label="Koszt km — tolerancja odczytów (dni)" description="odczyt najdalej tyle dni od początku i końca okresu" min={0} max={120} allowDecimal={false} disabled={!editable} value={s.costPerKmToleranceDays} onChange={(x) => typeof x === 'number' && set({ costPerKmToleranceDays: x })} />
          <NumberInput label="Konflikty z planem — ile dni naprzód" min={1} max={90} allowDecimal={false} disabled={!editable} value={s.conflictDays} onChange={(x) => typeof x === 'number' && set({ conflictDays: x })} />
        </SimpleGrid>
        <TagsInput label="Rodzaje / przeznaczenie pojazdów" disabled={!editable} value={s.vehicleKinds} onChange={(v) => set({ vehicleKinds: v.map((x) => x.trim()).filter(Boolean) })} />
      </Stack>
    </SectionCard>
  );
}

/** Ustawienia floty: rodzaje czynności i progi przypomnień (wszystko edytowalne, nic na sztywno w kodzie). */
export function FleetSettingsPage({ me }: { me: MeDto }) {
  const types = useActivityTypes();
  const settings = useFleetSettings();
  const editable = can(me, 'flota.zarzadzanie');
  const [editing, setEditing] = useState<ActivityTypeDto | 'new' | null>(null);
  return (
    <FleetShell me={me}>
      <PageHeader title="Ustawienia floty" description="Rodzaje czynności (bez wpisanych interwałów — cykl ustala zarządzający dla konkretnego pojazdu) i progi przypomnień." />
      <Stack gap="md">
        <SectionCard
          title="Rodzaje czynności"
          padding={0}
          actions={
            editable && (
              <Button size="xs" leftSection={<IconPlus size={14} />} onClick={() => setEditing('new')}>
                Dodaj rodzaj
              </Button>
            )
          }
        >
          <QueryState isLoading={types.isLoading} error={types.error}>
            {types.data?.length ? (
              <Table highlightOnHover>
                <Table.Thead>
                  <Table.Tr>
                    <Table.Th>Nazwa</Table.Th>
                    <Table.Th>Sposób wyznaczania</Table.Th>
                    <Table.Th>Podpowiedź cyklu</Table.Th>
                    <Table.Th />
                  </Table.Tr>
                </Table.Thead>
                <Table.Tbody>
                  {types.data.map((t) => (
                    <Table.Tr key={t.id} style={!t.active ? { opacity: 0.55 } : undefined}>
                      <Table.Td>
                        <Text size="sm" fw={500}>
                          {t.name}
                        </Text>
                        {!t.active && (
                          <Badge size="xs" color="gray" variant="light">
                            nieaktywny
                          </Badge>
                        )}
                      </Table.Td>
                      <Table.Td>
                        <Text size="sm">{OBLIGATION_RULES[t.rule]}</Text>
                      </Table.Td>
                      <Table.Td>
                        <Text size="sm">{[t.everyMonths ? `co ${t.everyMonths} mies.` : null, t.everyKm ? `co ${kmPl(t.everyKm)}` : null].filter(Boolean).join(' / ') || '—'}</Text>
                      </Table.Td>
                      <Table.Td ta="right">
                        {editable && (
                          <Button size="compact-sm" variant="subtle" onClick={() => setEditing(t)}>
                            Edytuj
                          </Button>
                        )}
                      </Table.Td>
                    </Table.Tr>
                  ))}
                </Table.Tbody>
              </Table>
            ) : (
              <EmptyState compact title="Brak rodzajów czynności" />
            )}
          </QueryState>
        </SectionCard>
        <QueryState isLoading={settings.isLoading} error={settings.error}>
          {settings.data && <ThresholdsCard settings={settings.data} editable={editable} />}
        </QueryState>
      </Stack>
      {editing && <TypeModal type={editing === 'new' ? null : editing} onClose={() => setEditing(null)} />}
    </FleetShell>
  );
}
